import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ModelUpload.css";
import {
	Box,
	Paper,
	Typography,
	Button,
	IconButton,
	Autocomplete,
	TextField,
	MenuItem,
	Divider,
	Tooltip,
	Switch,
	FormControlLabel,
	LinearProgress,
	Alert,
	CircularProgress,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import Swal from "sweetalert2";
import {
	getMachineModelFiles,
	uploadMachineModels,
	deleteMachineModelFile,
	replaceMachineModelFile,
} from "../../services/modelUploadService";
import { ALERT_MESSAGES, BUTTON, LABELS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getAllMachineModels } from "../../services/adminService";
import { toast } from "../../context/ToastContext";
import HeaderTitle from "../HeaderTitle/HeaderTitle";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import DocCard from "../DocCard/DocCard";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import { useAuth } from "../../context/authContext";

const ACCEPTED_EXTENSIONS = [".stp", ".step"];

const TYPE_CONFIG = {
	machine_model: {
		allowMultiple: false,
		renameable: false,
	},
	split: {
		allowMultiple: true,
		renameable: true,
	},
	bom: {
		allowMultiple: true,
		renameable: false,
	},
};

const getFileBaseName = (fileName = "") => {
	if (!fileName) return "";
	const idx = fileName.lastIndexOf(".");
	return idx > 0 ? fileName.slice(0, idx) : fileName;
};

const formatBytes = (bytes = 0) => {
	if (!bytes) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	const value = bytes / Math.pow(1024, exponent);
	return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const normalizeDateValue = (value) => {
	if (!value) return null;
	if (value instanceof Date) return value.toISOString();
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const computeLatestIsoDate = (values = []) => {
	const timestamps = values
		.map((value) => {
			const iso = normalizeDateValue(value);
			return iso ? new Date(iso).getTime() : null;
		})
		.filter((time) => typeof time === "number");
	if (!timestamps.length) return null;
	return new Date(Math.max(...timestamps)).toISOString();
};

const formatDateForDisplay = (value) => {
	const iso = normalizeDateValue(value);
	if (!iso) return null;
	const parsed = dayjs(iso);
	return parsed.isValid() ? parsed.format("MM-DD-YYYY HH:mm") : null;
};

const groupOrder = (t) => [
	{ key: "machine_model", label: t("UI_TEXT.MACHINE_MODEL") || "Machine Model" },
	{ key: "split", label: t("UI_TEXT.SPLIT_FILES") || "Split Files" },
	{ key: "bom", label: t("UI_TEXT.BOM_FILES") || "BOM Files" },
	{ key: "change_part", label: t("UI_TEXT.CHANGE_PART_FILES") || "Change Part Files" },
	{ key: "legacy", label: t("UI_TEXT.LEGACY_FILES") || "Legacy Files" },
];

const ModelUpload = () => {
	const { t } = useTranslation();
	const [allMachines, setAllMachines] = useState([]);
	const [machinesLoading, setMachinesLoading] = useState(true);
	const [machineSelected, setMachineSelected] = useState(null);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const { user, hasPermission } = useAuth();
	const [selectedFileType, setSelectedFileType] = useState("");
	const [pendingFiles, setPendingFiles] = useState([]);
	const [replaceExisting, setReplaceExisting] = useState(false);
	const [uploadSummary, setUploadSummary] = useState({});
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	const [showFilesPopup, setShowFilesPopup] = useState(false);
	const [selectedMachineForFiles, setSelectedMachineForFiles] = useState(null);
	const [modalFiles, setModalFiles] = useState([]);
	const [modalSummary, setModalSummary] = useState({});
	const [modalLoading, setModalLoading] = useState(false);

	const fileInputRef = useRef(null);

	const fileTypeOptions = useMemo(
		() => [
			{
				value: "machine_model",
				label: t("UI_TEXT.MACHINE_MODEL") || "Machine Model",
				description: t("UI_TEXT.MACHINE_MODEL_UPLOAD_DESC") || "Upload one base machine model per machine.",
			},
			{
				value: "split",
				label: t("UI_TEXT.SPLIT_FILES") || "Split Files",
				description: t("UI_TEXT.SPLIT_FILE_UPLOAD_DESC") || "Upload multiple split model files. You can rename each file before upload.",
			},
			{
				value: "bom",
				label: t("UI_TEXT.BOM_FILES") || "BOM Files",
				description: t("UI_TEXT.BOM_FILE_UPLOAD_DESC") || "Upload supplemental BOM assets and retain their original filenames.",
			},
		],
		[t]
	);

	const currentTypeConfig = TYPE_CONFIG[selectedFileType] || null;
	const currentSummary = uploadSummary[selectedFileType] || {};

	const updateMachineCountsFromSummary = useCallback(
		(machineNumber, summary = {}) => {
			if (!machineNumber || !summary) return;
			const targetKey = String(machineNumber);
			setAllMachines((prev) =>
				prev.map((machine) => {
					if (String(machine.machine_number) !== targetKey) return machine;

					const machineModelCount =
						typeof summary?.machine_model?.count === "number"
							? summary.machine_model.count
							: machine.machine_model_file_count || 0;
					const splitCount =
						typeof summary?.split?.count === "number"
							? summary.split.count
							: machine.split_file_count || 0;
					const bomCount =
						typeof summary?.bom?.count === "number"
							? summary.bom.count
							: machine.bom_file_count || 0;

					const machineModelLast =
						summary?.machine_model?.lastUpdated
							? normalizeDateValue(summary.machine_model.lastUpdated)
							: normalizeDateValue(machine.machine_model_last_updated);
					const splitLast =
						summary?.split?.lastUpdated
							? normalizeDateValue(summary.split.lastUpdated)
							: normalizeDateValue(machine.split_last_updated);
					const bomLast =
						summary?.bom?.lastUpdated
							? normalizeDateValue(summary.bom.lastUpdated)
							: normalizeDateValue(machine.bom_last_updated);

					const aggregatedLatest =
						computeLatestIsoDate([
							machineModelLast,
							splitLast,
							bomLast,
							machine.cp_last_updated || machine.change_part_last_updated || machine.machine_last_updated,
						]) || null;

					const changePartCount =
						typeof summary?.change_part?.count === "number"
							? summary.change_part.count
							: machine.change_part_file_count || machine.cp_file_count || 0;
					const totalCount = machineModelCount + splitCount + bomCount + changePartCount;

					return {
						...machine,
						machine_model_file_count: machineModelCount,
						machine_model_last_updated: machineModelLast,
						split_file_count: splitCount,
						split_last_updated: splitLast,
						bom_file_count: bomCount,
						bom_last_updated: bomLast,
						total_file_count: totalCount,
						machine_last_updated: aggregatedLatest,
					};
				})
			);
		},
		[setAllMachines]
	);

	const fetchMachines = async () => {
		setMachinesLoading(true);
		try {
			const response = await getAllMachineModels();
			if (response.status) {
				setAllMachines(response.data);
			} else {
				setAllMachines([]);
			}
		} catch (error) {
			setAllMachines([]);
		} finally {
			setMachinesLoading(false);
		}
	};

	useEffect(() => {
		fetchMachines();
	}, []);

	const resetUploadState = () => {
		setPendingFiles([]);
		setSelectedFileType("");
		setReplaceExisting(false);
		setUploadSummary({});
		setMachineSelected(null);
		setIsDragging(false);
		setUploading(false);
		setSummaryLoading(false);
		setUploadProgress(0);
	};

	const handleCloseUploadModal = () => {
		if (uploading) return;
		setUploadModalOpen(false);
		resetUploadState();
	};

	const fetchMachineSummary = async (machineNumber) => {
		if (!machineNumber) return;
		setSummaryLoading(true);
		try {
			const res = await getMachineModelFiles(machineNumber);
			const summary = res.data?.summary || {};
			setUploadSummary(summary);
			updateMachineCountsFromSummary(machineNumber, summary);
		} catch (error) {
			setUploadSummary({});
		} finally {
			setSummaryLoading(false);
		}
	};

	const fetchFilesForModal = async (machineNumber) => {
		if (!machineNumber) {
			setModalFiles([]);
			setModalSummary({});
			return;
		}
		setModalLoading(true);
		try {
			const res = await getMachineModelFiles(machineNumber);
			setModalFiles(res.data?.files || []);
			const summary = res.data?.summary || {};
			setModalSummary(summary);
			updateMachineCountsFromSummary(machineNumber, summary);
		} catch (error) {
			setModalFiles([]);
			setModalSummary({});
		} finally {
			setModalLoading(false);
		}
	};

	const groupedModalFiles = useMemo(() => {
		return modalFiles.reduce((acc, file) => {
			const key = file.type || "legacy";
			if (!acc[key]) acc[key] = [];
			acc[key].push(file);
			return acc;
		}, {});
	}, [modalFiles]);

	const assertUploadContext = () => {
		if (!machineSelected) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("UI_TEXT.SELECT_MACHINE_FIRST") || "Please select a machine to continue.",
			});
			return false;
		}
		if (!selectedFileType) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("UI_TEXT.SELECT_FILE_TYPE") || "Please choose a file type.",
			});
			return false;
		}
		return true;
	};

	const buildFileEntry = (file, type) => ({
		id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
		file,
		originalName: file.name,
		displayName: TYPE_CONFIG[type]?.renameable ? getFileBaseName(file.name) : file.name,
	});

	const handleFiles = (fileList) => {
		if (!assertUploadContext()) return;
		const files = Array.from(fileList || []);
		if (!files.length) return;

		const accepted = files.filter((file) => {
			const ext = `.${file.name.split(".").pop() || ""}`.toLowerCase();
			return ACCEPTED_EXTENSIONS.includes(ext);
		});

		if (!accepted.length) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.INVALID_FILE_FORMAT") || "Unsupported file type. Please upload .stp or .step files.",
			});
			return;
		}

		const newEntries = accepted.map((file) => buildFileEntry(file, selectedFileType));

		if (!currentTypeConfig?.allowMultiple) {
			setPendingFiles(newEntries.slice(0, 1));
		} else {
			setPendingFiles((prev) => {
				const existingNames = new Set(prev.map((entry) => entry.originalName));
				const merged = [...prev];
				newEntries.forEach((entry) => {
					if (!existingNames.has(entry.originalName)) {
						merged.push(entry);
						existingNames.add(entry.originalName);
					}
				});
				return merged;
			});
		}
	};

	const handleRemoveFile = (id) => {
		setPendingFiles((prev) => prev.filter((entry) => entry.id !== id));
	};

	const handleMachineChange = (_event, value) => {
		setMachineSelected(value);
		setSelectedFileType("");
		setPendingFiles([]);
		setReplaceExisting(false);
		setUploadSummary({});
		if (value?.machine_number) {
			fetchMachineSummary(value.machine_number);
		}
	};

	const handleUpload = async () => {
		if (!assertUploadContext()) return;
		if (!pendingFiles.length) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.PLEASE_SELECT_FILE"),
			});
			return;
		}

		if (selectedFileType === "split") {
			const hasEmptyNames = pendingFiles.some((file) => !file.displayName?.trim());
			if (hasEmptyNames) {
				Swal.fire({
					icon: "error",
					title: t("ALERT_MESSAGES.ERROR"),
					text: t("UI_TEXT.EMPTY_FILENAME_ERROR") || "Please enter a name for each split file.",
				});
				return;
			}
		}

		if (!currentTypeConfig?.allowMultiple && pendingFiles.length > 1) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.ONLY_ONE_FILE_UPLOAD"),
			});
			return;
		}

		setUploading(true);
		setUploadProgress(0);
		const formData = new FormData();
		pendingFiles.forEach((entry) => {
			formData.append("files", entry.file);
		});

		formData.append("machineNumber", machineSelected.machine_number);
		formData.append("fileType", selectedFileType);
		formData.append("replaceExisting", replaceExisting ? "true" : "false");

		if (selectedFileType === "split") {
			formData.append(
				"fileMetadata",
				JSON.stringify(
					pendingFiles.map((entry) => ({
						displayName: entry.displayName,
						name: entry.originalName,
					}))
				)
			);
		}

		try {
			const response = await uploadMachineModels(formData, {
				onUploadProgress: (event) => {
					if (!event || !event.total) {
						setUploadProgress((prev) => (prev >= 95 ? prev : prev + 1));
						return;
					}
					const percent = Math.min(100, Math.round((event.loaded * 100) / event.total));
					setUploadProgress(percent);
				},
			});
			if (response.status) {
				setUploadProgress(100);
				Swal.fire({
					icon: "success",
					title: t("ALERT_MESSAGES.SUCCESS"),
					text: t("ALERT_MESSAGES.MODEL_FILE_UPLOAD_SUCCESSFULLY"),
				});
				await fetchMachines();
				await fetchMachineSummary(machineSelected.machine_number);
				resetUploadState();
				setUploadModalOpen(false);
			} else {
				throw new Error("Upload failed");
			}
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.FAILED_MODEL_UPLOAD_FILE"),
			});
			console.error("Error during upload:", error);
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragging(false);
		if (!fileInputRef.current) return;
		if (!assertUploadContext()) return;
		const files = event.dataTransfer?.files;
		if (files?.length) {
			handleFiles(files);
		}
	};

	const handleRowClick = async (selected) => {
		const machine = selected?.row;
		if (!machine) return;
		setSelectedMachineForFiles(selected);
		setShowFilesPopup(true);
		await fetchFilesForModal(machine.machine_number);
	};

	const handleDeleteFile = async (file) => {
		const machineNumber = selectedMachineForFiles?.row?.machine_number;
		if (!machineNumber) return;
		const result = await Swal.fire({
			title: t("ALERT_MESSAGES.ARE_YOU_SURE") || "Are you sure?",
			text: `${t("UI_TEXT.DELETE_FILE_CONFIRM") || "Delete file"} "${file.name}"?`,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: t("BUTTON.DELETE") || "Delete",
		});

		if (!result.isConfirmed) return;

		try {
			const res = await deleteMachineModelFile(machineNumber, file.key);
			if (res.status) {
				toast.trigger({ message: t("ALERT_MESSAGES.FILE_DELETED_SUCCESSFULLY"), severity: "success" });
				await fetchFilesForModal(machineNumber);
				if (machineSelected?.machine_number === machineNumber) {
					fetchMachineSummary(machineNumber);
				}
			} else {
				throw new Error();
			}
		} catch (error) {
			toast.trigger({ message: t("ALERT_MESSAGES.FAILED_TO_DELETE_FILE"), severity: "error" });
		}
	};

	const handleReplaceFile = async (file, event) => {
		const machineNumber = selectedMachineForFiles?.row?.machine_number;
		if (!machineNumber) return;
		const replacement = event.target.files?.[0];
		event.target.value = "";
		if (!replacement) return;

		const formData = new FormData();
		formData.append("file", replacement);
		try {
			const res = await replaceMachineModelFile(machineNumber, file.key, formData);
			if (res.status) {
				toast.trigger({ message: t("ALERT_MESSAGES.FILE_REPLACED_SUCCESSFULLY"), severity: "success" });
				await fetchFilesForModal(machineNumber);
				if (machineSelected?.machine_number === machineNumber) {
					fetchMachineSummary(machineNumber);
				}
			} else {
				throw new Error();
			}
		} catch (error) {
			toast.trigger({ message: t("ALERT_MESSAGES.FAILED_TO_REPLACE"), severity: "error" });
		}
	};

	const shouldShowReplaceToggle =
		selectedFileType &&
		currentTypeConfig?.allowMultiple &&
		(currentSummary?.count || 0) > 0;

	const disableDropzone = !machineSelected || !selectedFileType;
	const isUploadDisabled =
		!machineSelected ||
		!selectedFileType ||
		!pendingFiles.length ||
		uploading ||
		(selectedFileType === "split" && pendingFiles.some((file) => !file.displayName?.trim()));

	const groupedSections = groupOrder(t);

	const renderDateCell = (params) => {
		const formatted = formatDateTime(params?.value);
		return formatted ? (
			<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

				<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>

					<Typography variant="body2">{formatted}</Typography>
				</Box>
			</Box>
		) : (
			<span style={{ fontSize: "12px", color: "gray" }}>—</span>
		);
	};

	return (
		<>
			<Box display="flex" flexDirection="row" width="100%" justifyContent="space-between" mb={2}>
				<HeaderTitle title={t("UI_TEXT.MANAGE_MACHINE_MODEL")} subtitle={t("UI_TEXT.ADMIN_UPLOAD_MODEL_PARTS")} />
			</Box>

			{machinesLoading ? (
				<DatagridLoading
					message={t("UI_TEXT.LOADING_MODELS", { defaultValue: "Loading available models..." })}
					actionButtons={1}
					columns={5}
					rows={6}
				/>
			) : (
				<Datagrid
					data={allMachines}
					columns={[
						{
							field: "machine_number",
							headerName: t("TABLE_HEADERS.MACHINE_NUMBER"),
							width: 200,
							align: "left",
							headerAlign: "left",
						},
						{ field: "machine_name", headerName: t("TABLE_HEADERS.MACHINE_NAME"), flex: 1 },
						{
							field: "total_file_count",
							headerName: t("TABLE_HEADERS.MACHINE_MODEL_FILE"),
							width: 180,
							align: "center",
							headerAlign: "center",
							renderCell: (params) =>
								(params?.row?.machine_model_file_count || 0),
						},
						{
							field: "machine_model_last_updated",
							headerName: t("TABLE_HEADERS.MACHINE_MODEL_LAST_UPDATE"),
							width: 220,
							align: "center",
							headerAlign: "center",
							renderCell: renderDateCell,
						},
						{
							field: "split_file_count",
							headerName: t("UI_TEXT.SPLIT_FILES"),
							width: 140,
							align: "center",
							headerAlign: "center",
							renderCell: (params) => params?.row?.split_file_count || 0,
						},
						{
							field: "split_last_updated",
							headerName: t("TABLE_HEADERS.SPLIT_LAST_UPDATE"),
							width: 220,
							align: "center",
							headerAlign: "center",
							renderCell: renderDateCell,
						},
						{
							field: "bom_file_count",
							headerName: t("UI_TEXT.BOM_FILES"),
							width: 140,
							align: "center",
							headerAlign: "center",
							renderCell: (params) => params?.row?.bom_file_count || 0,
						},
						{
							field: "bom_last_updated",
							headerName: t("TABLE_HEADERS.BOM_LAST_UPDATE"),
							width: 220,
							align: "center",
							headerAlign: "center",
								renderCell: renderDateCell,
							},
						]}
						key="machine_id"
						rowIdField="machine_id"
						customButtons={
							hasPermission('app_settings.allow_model_uploads') ?
								[{
									label: t("LABELS.ADD_NEW_MODEL"),
									icon: <FileUploadIcon />,
									onClick: () => setUploadModalOpen(true),
								}].flat() : []
						}
						pageSize={50}
						sortBy={[{ field: "machine_name", sort: "asc" }]}
					rowClick
					onRowClick={handleRowClick}
				/>
			)}

			<ModalWrapper
				open={uploadModalOpen}
				setOpen={setUploadModalOpen}
				onClose={handleCloseUploadModal}
				title={t("LABELS.ADD_NEW_MODEL") || "Add New Model"}
				width="640px"
				content={
					uploading ? (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 2,
								py: 4,
							}}
						>
							<Typography variant="h6" textAlign="center">
								{t("UI_TEXT.UPLOAD_IN_PROGRESS") || "Uploading models..."}
							</Typography>
							<Typography variant="body2" color="text.secondary" textAlign="center">
								{t("UI_TEXT.UPLOAD_PROGRESS_HINT") || "Please wait while your files are uploaded."}
							</Typography>
							<Box sx={{ width: "100%", maxWidth: 420 }}>
								<LinearProgress
									variant="determinate"
									value={Math.min(100, Math.max(0, uploadProgress))}
									sx={{ height: 10, borderRadius: 2 }}
								/>
							</Box>
							<Typography variant="body1" fontWeight={600}>
								{`${Math.min(100, Math.max(0, uploadProgress))}%`}
							</Typography>
						</Box>
					) : (
						<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
							<Autocomplete
								size="small"
								value={machineSelected}
								options={allMachines}
								getOptionLabel={(option) => `${option.machine_number} - ${option.machine_name}`}
								onChange={handleMachineChange}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("LABELS.SELECT_MACHINE")}
										variant="outlined"
									/>
								)}
							/>

							<TextField
								select
								size="small"
								label={t("LABELS.FILE_TYPE") || "File Type"}
								value={selectedFileType}
								onChange={(event) => {
									setSelectedFileType(event.target.value);
									setPendingFiles([]);
									setReplaceExisting(false);
								}}
									disabled={!machineSelected}
								>
									{fileTypeOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>

								{selectedFileType && (
									<Box>
										<Typography variant="body2" color="text.secondary">
											{fileTypeOptions.find((option) => option.value === selectedFileType)?.description}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{t("UI_TEXT.ACCEPTED_FORMATS") || "Accepted formats"}: {ACCEPTED_EXTENSIONS.join(", ")}
										</Typography>
									</Box>
								)}

								{summaryLoading ? (
									<Box display="flex" alignItems="center" gap={1}>
										<CircularProgress size={18} />
										<Typography variant="body2" color="text.secondary">
											{t("UI_TEXT.LOADING")}...
										</Typography>
									</Box>
								) : null}

								<Paper
									elevation={0}
									className={`upload-card ${isDragging ? "dragging" : ""}`}
									onDragOver={(event) => {
										event.preventDefault();
										if (disableDropzone) return;
										setIsDragging(true);
									}}
									onDragLeave={() => setIsDragging(false)}
									onDrop={handleDrop}
									sx={{
										width: "100%",
										padding: 3,
										textAlign: "center",
										border: "2px dashed #cccccc",
										borderRadius: 4,
										backgroundColor: disableDropzone ? "#f5f5f5" : isDragging ? "#f0f7ff" : "#fafafa",
										opacity: disableDropzone ? 0.6 : 1,
										pointerEvents: disableDropzone ? "none" : "auto",
										transition: "all 0.2s ease",
									}}
								>
									<FileUploadIcon sx={{ fontSize: 40, color: "#888" }} />
									<Typography variant="h6" gutterBottom>
										{t("UI_TEXT.DRAG_DROP_FILE")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{currentTypeConfig?.allowMultiple
											? t("UI_TEXT.MULTI_FILE_HINT") || "You can select multiple files."
											: t("UI_TEXT.SINGLE_FILE_HINT") || "Only one file is allowed for this type."}
									</Typography>
									<input
										id="model-upload-input"
										accept={ACCEPTED_EXTENSIONS.join(",")}
										style={{ display: "none" }}
										type="file"
										multiple={Boolean(currentTypeConfig?.allowMultiple)}
										ref={fileInputRef}
										onChange={(event) => {
											handleFiles(event.target.files);
											event.target.value = "";
										}}
										disabled={disableDropzone}
									/>
									<Button
										type="button"
										variant="contained"
										startIcon={<AttachFileIcon />}
										sx={{ marginTop: 2 }}
										disabled={disableDropzone}
										onClick={() => fileInputRef.current?.click()}
									>
										{t("BUTTON.SELECT_FILES") || "Select Files"}
									</Button>
								</Paper>

								{pendingFiles.length > 0 && (
									<Box display="flex" flexDirection="column" gap={1}>
										{pendingFiles.map((entry) => (
											<DocCard
												key={entry.id}
												title={entry.displayName}
												subtitle={`${formatBytes(entry.file.size)} • ${entry.originalName}`}
												imageProps={{ type: "document" }}
												rightContent={
													<Tooltip title={t("BUTTON.REMOVE") || "Remove"}>
														<IconButton size="small" onClick={() => handleRemoveFile(entry.id)} sx={{ color: "#f44336" }}>
															<CloseIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												}
												editableTitle={
													currentTypeConfig?.renameable
														? {
															value: entry.displayName,
															onChange: (value) =>
																setPendingFiles((prev) =>
																	prev.map((file) =>
																		file.id === entry.id ? { ...file, displayName: value } : file
																	)
																),
															placeholder: t("UI_TEXT.ENTER_FILE_NAME") || "Enter filename",
														}
														: null
												}
											/>
										))}
									</Box>
								)}

								{selectedFileType === "machine_model" && currentSummary?.count > 0 && (
									<Alert severity="warning">
										{t("UI_TEXT.MACHINE_MODEL_EXISTS_WARNING") ||
											"A machine model already exists. Uploading a new file will replace the current file."}
									</Alert>
								)}

								{shouldShowReplaceToggle && (
									<Box>
										<FormControlLabel
											control={
												<Switch
													checked={replaceExisting}
													onChange={(event) => setReplaceExisting(event.target.checked)}
												/>
											}
											label={
												replaceExisting
													? t("UI_TEXT.REPLACE_EXISTING_FILES") || "Replace existing files"
													: t("UI_TEXT.APPEND_EXISTING_FILES") || "Add to existing files"
											}
										/>
										<Typography variant="caption" color="text.secondary" display="block">
											{replaceExisting
												? t("UI_TEXT.REPLACE_EXISTING_HINT") || "Existing files for this machine will be removed."
												: t("UI_TEXT.APPEND_EXISTING_HINT") || "New files will be added alongside existing files."}
										</Typography>
									</Box>
								)}
							</Box>
						)
				}
				actions={
					<>
						<Button variant="outlined" color="inherit" onClick={handleCloseUploadModal} disabled={uploading}>
							{t("BUTTON.CANCEL")}
						</Button>
						<Button variant="contained" color="primary" onClick={handleUpload} disabled={isUploadDisabled}>
							{uploading ? t("BUTTON.UPLOADING") : t("BUTTON.UPLOADS")}
						</Button>
					</>
				}
			/>

			<ModalWrapper
				open={showFilesPopup}
				setOpen={setShowFilesPopup}
				onClose={() => {
					setShowFilesPopup(false);
					if (selectedMachineForFiles?.row?.machine_number) {
						fetchFilesForModal(selectedMachineForFiles.row.machine_number);
					}
				}}
				title={t("UI_TEXT.EDIT_MODEL_FILES")}
				subtitle={selectedMachineForFiles?.row?.machine_name || ""}
				width="520px"
				content={(() => {
					const visibleSections = groupedSections.filter(
						(section) => (groupedModalFiles[section.key] || []).length > 0
					);

					return (
						<Box sx={{ width: "100%", maxHeight: 420, overflowY: "auto", px: 1 }}>
							{modalLoading ? (
								<Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
									<CircularProgress size={28} />
								</Box>
							) : modalFiles.length === 0 ? (
								<Alert severity="info" sx={{ mt: 1 }}>
									{t("UI_TEXT.NO_FILES_AVAILABLE") || "No files uploaded for this machine yet."}
								</Alert>
							) : (
								visibleSections.map((section, idx) => {
									const items = groupedModalFiles[section.key] || [];
									const isLast = idx === visibleSections.length - 1;

									return (
										<Box key={section.key} mb={2}>
											<Typography variant="subtitle2" sx={{ mb: 1 }}>
												{section.label}
												{modalSummary?.[section.key]?.count ? ` (${modalSummary[section.key].count})` : ""}
											</Typography>

											{items.map((file) => (
												<Box
													key={file.key}
													display="flex"
													alignItems="center"
													justifyContent="space-between"
													sx={{
														border: "1px solid #f0f0f0",
														borderRadius: 1,
														padding: 1,
														mb: 1,
													}}
												>
													<Box>
														<Typography variant="body2">{file.displayName || file.name}</Typography>
														<Typography variant="caption" color="text.secondary">
															{formatBytes(file.size)} •{" "}
															{file.updatedAt ? dayjs(file.updatedAt).format("MM-DD-YYYY HH:mm") : "—"}
														</Typography>
													</Box>

													<Box display="flex" alignItems="center" gap={1}>
														{hasPermission('app_settings.allow_model_uploads') && (
															<Tooltip title={t("BUTTON.REPLACE") || "Replace"}>
																<IconButton component="label" size="small" sx={{ color: "#1976d2" }}>
																	<AttachFileIcon fontSize="small" />
																	<input
																		type="file"
																		hidden
																		accept={ACCEPTED_EXTENSIONS.join(",")}
																		onChange={(event) => handleReplaceFile(file, event)}
																	/>
																</IconButton>
															</Tooltip>
														)}
														{hasPermission('app_settings.allow_model_uploads') && (
															<Tooltip title={t("BUTTON.DELETE") || "Delete"}>
																<IconButton size="small" color="error" onClick={() => handleDeleteFile(file)}>
																	<DeleteIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														)}
													</Box>
												</Box>
											))}

											{!isLast && <Divider sx={{ mt: 2 }} />}
										</Box>
									);
								})
							)}
						</Box>
					);
				})()}
				actions={
					<Button
						variant="contained"
						onClick={() => {
							if (selectedMachineForFiles?.row?.machine_number) {
								fetchFilesForModal(selectedMachineForFiles.row.machine_number);
							}
							setShowFilesPopup(false);
						}}
					>
						{t("BUTTON.CLOSE")}
					</Button>
				}
			/>

		</>
	);
};

export default ModelUpload;
